import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './LiveSessionDetails.css';

const LiveSessionDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id, sessionId } = useParams();

  // Mock data - Single session details
  const session = {
    id: 1,
    title: 'Session 1: What is Calculus?',
    courseTitle: 'Advanced Mathematics Live Course',
    instructor: 'Dr. Smith',
    description: 'This session introduces the fundamental concepts of calculus. We will explore limits, derivatives, and their applications in real-world problems.',
    date: '2024-02-10',
    time: '15:00',
    duration: '60 minutes',
    platform: 'BBB',
    enrolled: 45,
    maxStudents: 50,
    status: 'upcoming', // upcoming or past
    hasRecording: false,
    recordingUrl: null,
    materials: [
      { id: 1, type: 'PDF', title: 'Session Notes', size: '2.5 MB', icon: '📄' },
      { id: 2, type: 'Link', title: 'Additional Resources', icon: '🔗' }
    ]
  };

  const handleJoin = () => {
    navigate(`/online-course/${id}/session/${sessionId}/room`);
  };

  const handleWatchRecording = () => {
    // Navigate to recording player
    navigate(`/online-course/${id}/session/${sessionId}/recording`);
  };

  return (
    <div className="live-session-details-page">
      <header className="live-session-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>Session Details</h1>
        <div className="header-spacer"></div>
      </header>

      <div className="live-session-details-content">
        {/* Session Header */}
        <div className="session-header-card">
          <div className="session-icon-large">{session.status === 'upcoming' ? '🔴' : '📹'}</div>
          <h2 className="session-title-large">{session.title}</h2>
          <p className="session-course-title">{session.courseTitle}</p>
          <p className="session-instructor-large">By {session.instructor}</p>
        </div>

        {/* Session Info */}
        <div className="session-info-card">
          <h3 className="card-title">Session Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-icon">📅</span>
              <div className="info-content">
                <span className="info-label">Date</span>
                <span className="info-value">{new Date(session.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
            <div className="info-item">
              <span className="info-icon">🕐</span>
              <div className="info-content">
                <span className="info-label">Time</span>
                <span className="info-value">{session.time}</span>
              </div>
            </div>
            <div className="info-item">
              <span className="info-icon">⏱️</span>
              <div className="info-content">
                <span className="info-label">Duration</span>
                <span className="info-value">{session.duration}</span>
              </div>
            </div>
            <div className="info-item">
              <span className="info-icon">💻</span>
              <div className="info-content">
                <span className="info-label">Platform</span>
                <span className="info-value">{session.platform}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enrollment Status */}
        <div className="enrollment-card">
          <div className="enrollment-info">
            <span className="enrollment-count">{session.enrolled}/{session.maxStudents}</span>
            <span className="enrollment-label">Students Enrolled</span>
          </div>
          <div className="enrollment-progress-bar">
            <div 
              className="enrollment-progress-fill"
              style={{ width: `${(session.enrolled / session.maxStudents) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Description */}
        <div className="description-card">
          <h3 className="card-title">About This Session</h3>
          <p className="description-text">{session.description}</p>
        </div>

        {/* Materials */}
        {session.materials.length > 0 && (
          <div className="materials-card">
            <h3 className="card-title">Session Materials</h3>
            {session.materials.map((material) => (
              <div key={material.id} className="material-item">
                <div className="material-icon">{material.icon}</div>
                <div className="material-info">
                  <h4>{material.title}</h4>
                  {material.size && (
                    <span className="material-size">{material.size}</span>
                  )}
                </div>
                <button className="download-button">⬇</button>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="session-actions">
          {session.status === 'upcoming' && (
            <button className="join-session-button" onClick={handleJoin}>
              Join Live Session
            </button>
          )}
          {session.hasRecording && (
            <button className="watch-recording-button" onClick={handleWatchRecording}>
              Watch Recording
            </button>
          )}
          {session.status === 'past' && !session.hasRecording && (
            <div className="no-recording-message">
              <p>This session has ended and no recording is available.</p>
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default LiveSessionDetails;
